import { z } from 'zod';
import { createModel, currencySchema, partySchema, QueryMany, QueryOne } from '../../common';
import { dateSchema, idSchema, nameSchema, objectIdSchema, phoneSchema } from '../../validation';
import { emailSchema } from '../../validation/email';
import { languageSchema } from '../../common/language.schema';
import { conversationChannelTypeSchema } from '../conversation/conversation.model';

/**
 * Direction of the message relative to the org.
 *
 * - `inbound`  — the org received it (customer typing in chat,
 *                WhatsApp customer reply, etc.)
 * - `outbound` — the org / AI sent it.
 */
export const conversationMessageDirectionSchema = z.enum(['inbound', 'outbound']);
export type ConversationMessageDirection = z.infer<
  typeof conversationMessageDirectionSchema
>;

/**
 * Who sent the message. The `type` field discriminates how to interpret
 * the rest of the sender block.
 *
 * - `external`      — outside the platform (e.g. WhatsApp customer)
 * - `platform_user` — authenticated org user typing in chat
 * - `ai`            — Hero AI's outbound
 * - `staff`         — Hero internal staff / org admin replying via inbox
 * - `system`        — programmatic events (e.g. "Conversation reassigned")
 */
export const conversationMessageSenderSchema = z.object({
  type: z.enum(['external', 'platform_user', 'ai', 'staff', 'system']),
  userId: objectIdSchema.optional(),
  contactId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  displayName: nameSchema.optional(),
});
export type ConversationMessageSender = z.infer<
  typeof conversationMessageSenderSchema
>;

/**
 * LangChain replay role. Maps cleanly to LangChain `BaseMessage` so
 * we can pull a turn's transcript and feed it back to the LLM
 * verbatim.
 *
 * - `user`      — the user that triggered the AI turn.
 * - `assistant` — the AI's reply.
 * - `staff`     — a Hero/org human reply (treated as `user` for LLM
 *                 replay; kept distinct here for inbox UI).
 * - `system`    — programmatic note ("conversation reassigned").
 * - `tool`      — tool-result message bubble (rare; tool results are
 *                 usually emitted as widget blocks instead).
 */
export const conversationMessageRoleSchema = z.enum([
  'user',
  'assistant',
  'staff',
  'system',
  'tool',
]);
export type ConversationMessageRole = z.infer<typeof conversationMessageRoleSchema>;

export const conversationSystemNoteKindSchema = z.enum([
  'takeover',
  'hand_back_reset',
  'hand_back_keep',
  'staff_instruction',
  'staff_action',
]);
export type ConversationSystemNoteKind = z.infer<typeof conversationSystemNoteKindSchema>;

/**
 * Delivery status — meaningful for outbound messages on channels with
 * acknowledgement (WhatsApp, SMS, email). In-app outbound is `sent`
 * once the SSE event has been flushed.
 */
export const deliveryStatusSchema = z.enum([
  'pending',
  'sent',
  'delivered',
  'read',
  'failed',
  /**
   * Composed but never sent — the person wrote again while the reply was
   * being written, or the turn was aborted. Kept so the transcript shows
   * what the agent would have said; the follow-up turn answers instead.
   */
  'withheld',
]);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

/**
 * Where the bytes actually live. Media is split by kind: images and
 * PDFs stay on Cloudinary because we use its transformations (PDF page
 * rasterization for the identity reader), while audio goes to a
 * private GCS bucket read through short-lived signed URLs. See
 * [ADR-006](../../../../../docs/adr/006-media-storage-split-by-kind.md).
 *
 * `cloudinary` assets are publicly reachable at {@link
 * conversationAttachmentSchema.url}; `gcs` assets are not, and must be
 * resolved through the authenticated media endpoint.
 */
export const conversationAttachmentStorageSchema = z.object({
  provider: z.enum(['cloudinary', 'gcs']),
  bucket: idSchema.optional(),
  objectPath: z.string().optional(),
});
export type ConversationAttachmentStorage = z.infer<
  typeof conversationAttachmentStorageSchema
>;

/**
 * Speech-to-text output for an `audio` attachment, kept next to the
 * clip it came from rather than only as loose text, so the pair stays
 * queryable as training data.
 *
 * `languageCode` / `languageProbability` are the engine's own language
 * detection, which beats the script heuristic in
 * `resolve-agent-language.ts` — Urdu is written in Arabic script, and
 * callers mix Arabic, Urdu and English inside a single clip.
 */
/**
 * A short staff-facing summary of a voice note, in one language: what
 * the speaker wants (and how they sound), plus the facts copied exactly
 * from the transcript. The agent never reads it — it keeps the full
 * transcript. See `docs/ai/voice/summaries.md`.
 */
export const conversationVoiceSummarySchema = z.object({
  language: languageSchema,
  /** One or two lines, e.g. «يبي وثيقة من جدة إلى منفذ سلوى بكرة، والسائق هو نفسه». */
  text: z.string(),
  /** Cities, dates, phones, plates, names — verbatim substrings of the transcript, never translated. */
  facts: z.array(z.string()).default([]),
  model: idSchema,
  createdAt: dateSchema,
});
export type ConversationVoiceSummary = z.infer<typeof conversationVoiceSummarySchema>;

/**
 * A message translated for a staff reader, straight from the original
 * (never via English). Cached per language on the message.
 */
export const conversationMessageTranslationSchema = z.object({
  text: z.string(),
  model: idSchema,
  createdAt: dateSchema,
});
export type ConversationMessageTranslation = z.infer<typeof conversationMessageTranslationSchema>;

export const conversationAttachmentTranscriptSchema = z.object({
  text: z.string(),
  /**
   * Staff summaries keyed by language code. The inbox language is
   * written after the transcript is saved; others on demand. Notes
   * under ~15 words get none — the transcript is short enough to read.
   */
  summaries: z.record(z.string(), conversationVoiceSummarySchema).optional(),
  /** Provider id, e.g. `elevenlabs`. */
  provider: idSchema,
  /** Model id, e.g. `scribe_v2`. */
  model: idSchema.optional(),
  /** ISO-639-3 as reported by the engine, e.g. `ara` / `urd` / `eng`. */
  languageCode: idSchema.optional(),
  languageProbability: z.number().min(0).max(1).optional(),
  /**
   * The speaker's likely native language, when the engine judges accent
   * (Gemini audio). A Pakistani driver speaking Arabic is `languageCode:
   * 'ara'` with `speakerL1: 'ur'`.
   */
  speakerL1: languageSchema.optional(),
  /** How long the provider call took, not the length of the clip. */
  durationMs: z.number().int().nonnegative().optional(),
  transcribedAt: dateSchema,
});
export type ConversationAttachmentTranscript = z.infer<
  typeof conversationAttachmentTranscriptSchema
>;

/**
 * Vision output for an identity-document `image` / `pdf` attachment
 * (إقامة / رخصة قيادة / رخصة سير), kept next to the photo the same way
 * {@link conversationAttachmentTranscriptSchema} sits next to a clip.
 *
 * `data` is the full structured extract (`identityDocumentsExtractSchema`
 * in the backend reader). Confirmation reads it back by attachment URL
 * when a `propose_*` card is accepted, so every printed field lands on
 * `Driver.legal.extraction` / `Vehicle.registration.extraction` without
 * the model having to copy each one into a tool argument.
 */
export const conversationAttachmentIdentityExtractSchema = z.object({
  extractedAt: dateSchema,
  /** `iqama` / `license` / `istimara` — which cards vision saw in this image set. */
  documentsDetected: z.array(idSchema).default([]),
  data: z.record(z.unknown()),
});
export type ConversationAttachmentIdentityExtract = z.infer<
  typeof conversationAttachmentIdentityExtractSchema
>;

/**
 * An attachment block. Image / PDF / audio / file. AI never directly
 * persists raw bytes — ingest copies them to durable storage first
 * (see {@link conversationAttachmentStorageSchema}).
 */
export const conversationAttachmentSchema = z.object({
  type: z.enum(['image', 'pdf', 'audio', 'video', 'file']),
  url: z.string().url(),
  mimeType: idSchema.optional(),
  size: z.number().int().nonnegative().optional(),
  /** Page count Cloudinary reported for a PDF — how many pages vision reads. */
  pages: z.number().int().positive().optional(),
  name: nameSchema.optional(),
  storage: conversationAttachmentStorageSchema.optional(),
  transcript: conversationAttachmentTranscriptSchema.optional(),
  identityExtract: conversationAttachmentIdentityExtractSchema.optional(),
  /**
   * What vision read off THIS file alone, before the burst was merged into
   * one person (`identityExtract`). Fleet intake builds its roster from
   * these; `extractedAt` is shared by every file read in the same burst.
   */
  fileExtract: conversationAttachmentIdentityExtractSchema.optional(),
  /** A WhatsApp sticker (stored as an image, never OCR'd). */
  sticker: z.boolean().optional(),
  /** sha256 of the bytes as received — the sticker caption cache key. */
  sha256: z.string().optional(),
  /**
   * What the image shows, in English, for the model — a sticker's
   * caption (`praying hands, text "جزاك الله خير"`). Replays as
   * `[sticker: …]`. Never shown to the user.
   */
  caption: z.string().optional(),
});
export type ConversationAttachment = z.infer<typeof conversationAttachmentSchema>;

/**
 * GeoPoint in [lng, lat] — matches GoogleMap's expected format and
 * Mongo geospatial index conventions.
 */
const geoPointSchema = z.tuple([z.number(), z.number()]);

/**
 * Catalog of widgets the agent can surface inside a chat bubble. Each
 * variant carries the minimum data needed to render and to deep-link
 * to the existing entity drawer (`useOrderDrawerStore`,
 * `useClientDrawerStore`, etc.).
 *
 * `displaySnapshot` is an optional inline preview so the card renders
 * instantly from the SSE event before the entity hook resolves
 * (Vercel-AI-SDK style). The widget always re-fetches via the live
 * entity hook on render — see `docs/ai/widgets.md` "Live, not stale
 * snapshots".
 *
 * To add a widget: extend the union here, add a renderer in
 * `libs/frontend/web/components/src/feature/Agent/ChatPanel/widgets/`,
 * register it in `WidgetRenderer`, and update `docs/ai/widgets.md`.
 */
export const widgetSpecSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('order_card'),
    orderId: objectIdSchema,
    displaySnapshot: z
      .object({
        ref: idSchema.optional(),
        status: idSchema.optional(),
        total: z.number().optional(),
        currency: currencySchema.optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('order_list'),
    orderIds: z.array(objectIdSchema).min(1),
    title: nameSchema.optional(),
  }),
  z.object({
    type: z.literal('client_card'),
    clientId: objectIdSchema,
    displaySnapshot: z.object({ name: nameSchema.optional() }).optional(),
  }),
  z.object({
    type: z.literal('driver_card'),
    driverId: objectIdSchema,
    displaySnapshot: z
      .object({ name: nameSchema.optional(), phone: phoneSchema.optional() })
      .optional(),
  }),
  z.object({
    type: z.literal('vehicle_card'),
    vehicleId: objectIdSchema,
    displaySnapshot: z
      .object({ plate: idSchema.optional(), type: idSchema.optional() })
      .optional(),
  }),
  z.object({
    type: z.literal('saved_location_card'),
    savedLocationId: objectIdSchema,
    displaySnapshot: z.object({ name: nameSchema.optional() }).optional(),
  }),
  z.object({
    type: z.literal('route_map'),
    pickup: geoPointSchema,
    dropoff: geoPointSchema,
    distanceMeters: z.number().nonnegative().optional(),
    etaSeconds: z.number().nonnegative().optional(),
  }),
  z.object({
    type: z.literal('quote_breakdown'),
    subtotal: z.number(),
    fees: z.array(z.object({ label: nameSchema, amount: z.number() })).optional(),
    tax: z.object({ label: nameSchema, amount: z.number() }).optional(),
    total: z.number(),
    currency: currencySchema,
    agreementId: objectIdSchema.optional(),
  }),
  z.object({
    type: z.literal('confirmation_card'),
    confirmationId: idSchema,
    action: z.enum([
      'create_order',
      'create_client',
      'create_saved_location',
      'update_saved_location',
      'assign_driver_to_order',
      'assign_orders_to_vehicles',
      'create_financing_application',
      'accept_financing_offer',
      // OrderAgent lifecycle additions (foundation v1):
      'update_order_status',
      'escalate_order',
      // ContactAgent additions (foundation v1):
      'create_contact',
      'update_contact',
      'delete_contact',
      // SalesAgent lead capture (WhatsApp / guest personas):
      'create_lead',
      // OfferAgent load-board responses (Phase 5 expansion):
      'accept_offer',
      'decline_offer',
      'counter_offer',
      // Government e-waybill (Bayan via Logisti) — manual issuance:
      'issue_government_waybill',
      // Post-create order edits — price, fees, dates, destinations. The
      // bulk variant is one card standing for up to 50 orders.
      'edit_order',
      'bulk_edit_orders',
      // In-chat account creation + Logisti connection (Wathiqa Plus funnel
      // steps 2 and 3).
      'complete_signup',
      'connect_logisti',
      // Fleet creation from chat (Wathiqa Plus funnel step 4). The combined
      // action adds the driver and their truck as one confirmation —
      // a truck without a driver can't carry a waybill, so on a
      // waybill-first line the pair is the unit people think in.
      'create_driver',
      'create_vehicle',
      'create_driver_with_vehicle',
      // The owner-operator's equivalent of steps 3 and 4 together: he
      // has no CR to connect and no fleet to staff, so the one card
      // completes his own Driver with what Bayan requires and adds his
      // truck.
      'setup_owner_operator',
      // Subscription checkout from chat (Wathiqa Plus funnel step 6).
      'subscribe',
      // Reshape the caller's own account (driver ↔ company, company type).
      'change_account',
    ]),
    payload: z.record(z.unknown()),
    expiresAt: dateSchema.optional(),
  }),
  z.object({
    type: z.literal('task_progress_card'),
    taskId: objectIdSchema,
    total: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal('escalation_card'),
    reason: nameSchema,
    assignedTo: z
      .object({ userId: objectIdSchema.optional(), displayName: nameSchema.optional() })
      .optional(),
  }),
  /**
   * "Which language?" — three buttons, each titled in its own script.
   * Sent with no model call when a thread's first burst gives no clear
   * language, or on an explicit "change language". A pick pins the
   * thread (`languageSource: 'user_choice'`). Not an assistant reply:
   * the hello intro still counts as the first one.
   */
  z.object({
    type: z.literal('language_picker'),
    options: z.array(languageSchema).min(2),
  }),
]);
export type WidgetSpec = z.infer<typeof widgetSpecSchema>;
export type WidgetType = WidgetSpec['type'];

/**
 * One block inside `ConversationMessage.content.blocks[]`. Channels
 * that can't render a block type (WhatsApp can't show an interactive
 * map) fall back to `fallbackText`. In-app and (later) email render
 * the full thing.
 */
export const conversationMessageBlockSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('text'),
    text: z.string(),
    /**
     * Model-only memory: replayed to the LLM (so vision extracts survive
     * the next turn) but stripped from every human-facing transcript
     * (admin CRM inbox, in-app history, sidebar preview). Used for the
     * media-analysis note appended to an inbound message — it must not
     * render as a user bubble. Absent / false = normal visible text.
     */
    internal: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('attachment'),
    attachments: z.array(conversationAttachmentSchema).min(1),
  }),
  z.object({
    kind: z.literal('widget'),
    widget: widgetSpecSchema,
    fallbackText: z.string(),
  }),
]);
export type ConversationMessageBlock = z.infer<typeof conversationMessageBlockSchema>;

/**
 * LangChain-shaped tool call recorded on the message that requested it.
 * The result lands on the next message (`role: 'tool'` or as a
 * `widget` block on the assistant message, depending on rendering).
 */
export const toolCallRecordSchema = z.object({
  id: idSchema,
  name: idSchema,
  args: z.record(z.unknown()).default({}),
});
export type ToolCallRecord = z.infer<typeof toolCallRecordSchema>;

export const toolResultRecordSchema = z.object({
  toolCallId: idSchema,
  name: idSchema,
  result: z.unknown().optional(),
  error: z.string().optional(),
});
export type ToolResultRecord = z.infer<typeof toolResultRecordSchema>;

/**
 * How to actually put a queued outbound bubble on the wire.
 *
 * The queue *state* is `deliveryStatus` itself (`pending` → `sent` /
 * `failed`) rather than a second status field here — and because
 * `AgentMessageService.updateDeliveryStatusByExternalId` ranks `pending`
 * at 0 and only ever moves a row upward, a provider receipt can never
 * drag a delivered message back into the queue.
 *
 * **This field survives the terminal write.** It is not cleared when the
 * row settles, for two reasons: it carries {@link dedupeKey}, which has
 * to stay queryable for as long as a trigger could replay, and what is
 * left behind is an exact record of how the message went out. Read
 * `deliveryStatus` to ask whether a row is still queued; the presence of
 * this field answers a different question.
 *
 * This is the whole reason a producer needs to know nothing about Buraq:
 * it writes what the recipient will see into `content.blocks` and how to
 * send it here, and the drainer owns spacing, batching, retries and
 * closing the provider conversation.
 */
export const conversationMessageOutboundSchema = z.object({
  /**
   * `whatsapp_template` — an approved template / bot flow, legal at any
   * time. `whatsapp_session_text` — free-form text, legal only inside
   * the 24-hour window, which is therefore checked at drain time (the
   * window can close while a row waits).
   */
  transport: z.enum(['whatsapp_template', 'whatsapp_session_text']),
  /**
   * The Buraq bot webhook this template's flow starts from, resolved by
   * the producer. Carrying it on the row is what lets the drainer merge
   * consecutive rows that share a bot into one request without knowing
   * which registry the template came from.
   *
   * Absent for `whatsapp_session_text`, and for templates on a Meta
   * account — those route by approved template name instead, so the
   * drainer sends them one at a time through the template transport.
   */
  webhookUrl: z.string().optional(),
  templateId: idSchema.optional(),
  /** Named variables the bot expects, plus any header media key. */
  payload: z.record(z.string()).optional(),
  /** Language the provider resolves its approved copy variant from. */
  language: languageSchema.optional(),
  /**
   * `whatsapp_session_text` only: send as a Cloud interactive message
   * with the same Yes (`confirm`) or Yes + No (`confirm_cancel`) reply
   * buttons a staged card gets. Lets a question ride the queue behind
   * the documents it must follow instead of racing them inline.
   */
  replyButtons: z.enum(['confirm', 'confirm_cancel']).optional(),
  /** Recipient as enqueued; the dev redirect applies at send time. */
  to: phoneSchema,
  attempts: z.number().int().nonnegative().default(0),
  maxAttempts: z.number().int().positive().default(3),
  nextAttemptAt: dateSchema.optional(),
  errorMessage: z.string().optional(),
  /**
   * Producer-supplied natural key (unique sparse index), e.g.
   * `order_wathiqah:<orderId>:<phone>`.
   *
   * This is the only idempotency the trigger-fired producers have:
   * unlike the inbound webhook, which reserves `message.id` in
   * `IdempotencyKey`, `TriggersService.onTrigger` has none — so a
   * redelivered Atlas trigger used to send the document twice.
   */
  dedupeKey: idSchema.optional(),
  /**
   * The staff member who put this row on the wire — a manual reply from
   * the inbox, a ticket-close notification, a campaign send. Audit only;
   * the bubble's `sender` still says who the recipient saw it from.
   */
  sentBy: z
    .object({
      userId: objectIdSchema,
      displayName: nameSchema.optional(),
    })
    .optional(),
  /** The `MessageCampaign` run this row belongs to, for bulk sends. */
  campaignId: objectIdSchema.optional(),
  /** When the provider receipt moved `deliveryStatus` to each step — campaign analytics. */
  sentAt: dateSchema.optional(),
  deliveredAt: dateSchema.optional(),
  readAt: dateSchema.optional(),
});
export type ConversationMessageOutbound = z.infer<typeof conversationMessageOutboundSchema>;

/** Which code path put a WhatsApp message on the wire. */
export const conversationWireOriginSchema = z.enum([
  'model_turn',
  'confirm_result',
  'progress',
  'followup',
  'template',
  'staff',
  'campaign',
  'language_picker',
  'reaction',
  'media',
  'system',
]);
export type ConversationWireOrigin = z.infer<typeof conversationWireOriginSchema>;

/**
 * Exactly what one WhatsApp message carried when it left — written at
 * the single provider exit (`Message360dialogService.postMessages`),
 * never reconstructed from `content.blocks`.
 *
 * `content.blocks` is what the agent *meant* (model markdown, widgets);
 * the channel then regroups, splits, swaps in menu bodies and adds
 * buttons. This is what the phone *showed*. Admin transcripts, history
 * replay and follow-ups read this when present.
 */
export const conversationMessageWireSchema = z.object({
  origin: conversationWireOriginSchema,
  type: z.enum([
    'text',
    'interactive_buttons',
    'interactive_list',
    'interactive_cta',
    'interactive_location',
    'image',
    'document',
    'video',
    'audio',
    'template',
    'reaction',
  ]),
  /** Text body / caption / interactive body. */
  body: z.string().optional(),
  buttons: z.array(z.object({ id: z.string(), title: z.string() })).optional(),
  /** Interactive header (media link or text). */
  header: z
    .object({ type: z.string(), text: z.string().optional(), link: z.string().optional() })
    .optional(),
  media: z
    .object({ link: z.string().optional(), mediaId: z.string().optional(), filename: z.string().optional() })
    .optional(),
  template: z
    .object({
      name: z.string(),
      language: z.string(),
      /** `parameter_name → text` for named params, `"<component>.<index>"` for positional. */
      params: z.record(z.string()).optional(),
    })
    .optional(),
  reaction: z.object({ emoji: z.string(), targetWamid: z.string() }).optional(),
  /**
   * Fact tags this message carried to the user (`intro`, `price:19 SAR`,
   * `waybill:#743198`) — set by the sender from the turn's `mustInclude`.
   * The conversation brief's "already told" reads only these, so it
   * means the user really received it.
   */
  facts: z.array(z.string()).optional(),
  wamid: z.string().optional(),
  sentAt: dateSchema,
  status: z.enum(['sent', 'failed']),
  error: z.string().optional(),
});
export type ConversationMessageWire = z.infer<typeof conversationMessageWireSchema>;

/**
 * `ConversationMessage` — every chat bubble in a `Conversation`.
 *
 * The collection holds messages from every sender type
 * (`external` / `platform_user` / `ai` / `staff` / `system`). The
 * sibling `AgentActivity` collection is the structured operational
 * audit log; this collection is the user-visible transcript.
 *
 * See `docs/ai/messaging-model.md` and ADR-003 for the rename from
 * `AgentMessage`.
 */
export const ConversationMessageSchema = createModel({
  conversationId: objectIdSchema,
  /**
   * Denormalized from {@link ConversationSchema} for org-scoped
   * queries — same contract as there: ALWAYS a real Org
   * (`target: 'org'`), absent on guest / org-less threads. Filter
   * combos: by org (`'org._id'`), by user (`'sender.userId'`), by
   * user in org (both).
   */
  org: partySchema
    .refine(p => p.target === 'org', {
      message: 'ConversationMessage.org must reference a real Org (target "org")',
    })
    .optional(),
  direction: conversationMessageDirectionSchema,
  /** Flat provenance — the surface this bubble was sent on. */
  channel: conversationChannelTypeSchema,

  sender: conversationMessageSenderSchema,
  role: conversationMessageRoleSchema,
  /**
   * What a `role: 'system'` row records — see `docs/ai/human-takeover.md`.
   * `hand_back_reset` also moved `replayFrom`; `hand_back_keep` did not.
   * `staff_instruction` is a note to the AI from staff, loaded on every
   * turn apart from the replay window so it never scrolls out.
   * Absent on rows written before the field existed.
   */
  systemKind: conversationSystemNoteKindSchema.optional(),

  content: z.object({
    blocks: z.array(conversationMessageBlockSchema).min(1),
  }),

  externalMessageId: idSchema.optional(),
  /**
   * When the message was sent, not when this row was written — the order
   * every surface (agent transcript, admin, dashboard) sorts by, with
   * `_id` as the tie-break. Inbound: the provider's own timestamp
   * (WhatsApp `timestamp`, whole seconds), which can be well before our
   * webhook got it — a photo still uploading lands after a text typed
   * later. Outbound: the write time, moved to `wire.sentAt` once the
   * provider accepts it. Rows from before the field: `doc.createdAt`.
   */
  messageDate: dateSchema,
  deliveryStatus: deliveryStatusSchema.optional(),

  /**
   * How this bubble was (or is to be) put on the wire. Written together
   * with `deliveryStatus: 'pending'` and kept after it settles — see
   * {@link conversationMessageOutboundSchema}.
   */
  outbound: conversationMessageOutboundSchema.optional(),

  /**
   * Staff translations of this message keyed by language code, made on
   * demand from the original text. The original is never replaced.
   */
  translations: z.record(z.string(), conversationMessageTranslationSchema).optional(),

  /** What the phone received for this row — see {@link conversationMessageWireSchema}. */
  wire: conversationMessageWireSchema.optional(),
  /**
   * The row this one was split from: a preamble bubble before a card, a
   * second bubble of a split reply, a confirm progress line. One
   * WhatsApp message = one row; the parent keeps the composed blocks.
   */
  parentMessageId: objectIdSchema.optional(),
  /**
   * The row exists only to record a WhatsApp message that had no row of
   * its own (a split bubble, a progress line). Its text is already part
   * of the parent's blocks, so model replay skips it.
   */
  wireOnly: z.boolean().optional(),

  toolCalls: z.array(toolCallRecordSchema).optional(),
  toolResults: z.array(toolResultRecordSchema).optional(),

  /**
   * Set when this assistant bubble is the localized apology for a failed
   * run — the text the user actually saw, persisted BEFORE it is sent
   * like every other bubble (persist-before-send). `code` is the public
   * error class; `referenceId` is the Sentry event id (and `ticketRef`
   * the support ticket) an operator can chase. The raw error never
   * lands here.
   */
  failure: z
    .object({
      code: z.string(),
      referenceId: z.string().optional(),
      ticketRef: z.string().optional(),
    })
    .optional(),

  /**
   * A mid-turn status bubble the user saw but was never asked to answer
   * — the triage ack («استقبلت الطلب، أسند للسواق الحين»), the 12s
   * progress line, the write-progress line («جاري تجهيز الطلب… ثواني ⏳»).
   *
   * Persisted like every other bubble (persist-before-send; the
   * transcript must hold what the phone saw) but flagged so it is NOT
   * the "newest visible assistant message" that a bare yes/no or an
   * amend resolves against. Unflagged, the write-progress line landed
   * in the collection a few ms before `amend_staged_create_order` ran,
   * `loadPendingConfirmationCards` anchored on it, saw no card, and the
   * tool redirected the edit onto a *different*, already-confirmed
   * order (production, 16 Sep 2026, conv 6aa6056d…).
   */
  transient: z.boolean().optional(),

  /**
   * The {@link AgentRunSchema} that produced this message (if any).
   * Set on assistant + tool messages; absent on inbound user / staff
   * messages. Used to query "all bubbles produced by this turn" for
   * SSE replay and observability.
   */
  runId: objectIdSchema.optional(),
});

export type ConversationMessage = z.infer<typeof ConversationMessageSchema.model>;
export type ConversationMessageData = z.infer<typeof ConversationMessageSchema.data>;
export type ConversationMessagePartial = z.infer<
  typeof ConversationMessageSchema.partial
>;
export type ConversationMessageQueryOne = QueryOne<ConversationMessage>;
export type ConversationMessageQueryMany = QueryMany<ConversationMessage>;
